import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { ArrowLeft, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { PanelSkeleton } from '@/components/ui/skeleton';
import { ApiErrorSummary } from '@/components/ApiErrorSummary';
import { EditUserDetailsForm } from '../components/EditUserDetailsForm';
import { EditChangePasswordForm } from '../components/EditChangePasswordForm';
import { getApiErrorMessage, getApiErrorDetails, type ApiErrorDetails } from '@/lib/getApiErrorMessage';
import { userService } from '../services';
import type { UserDto } from '../types';
import type { EditUserDetailsFormData, ChangePasswordFormData } from '../schemas';

export function EditUserPage() {
  usePageTitle('Edit User');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<UserDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detailsError, setDetailsError] = useState<ApiErrorDetails | null>(null);
  const [passwordError, setPasswordError] = useState<ApiErrorDetails | null>(null);

  useEffect(() => {
    if (!id) return;

    const fetchUser = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const userData = await userService.getUserById(Number(id));
        setUser(userData);
      } catch (err) {
        setError(getApiErrorMessage(err, 'Failed to fetch user'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchUser();
  }, [id]);

  const handleBack = () => {
    navigate('/administration/users');
  };

  const handleEditDetailsSubmit = async (data: EditUserDetailsFormData) => {
    if (!user) return;
    setDetailsError(null);
    try {
      const updatedUser = await userService.updateUser(user.id, {
        email: data.email,
        firstName: data.firstName,
        middleName: data.middleName || '',
        lastName: data.lastName,
        mobileNumber: data.mobileNumber || '',
        startDate: data.startDate || null,
        birthdate: data.birthdate || null,
        isActive: data.isActive,
        // Employment info fields are read-only in the edit form — preserve existing values
        employeeIdNumber: user.employeeIdNumber || '',
        departmentId: user.departmentId || null,
        departmentName: user.departmentName || null,
        positionId: user.positionId || null,
        positionName: user.positionName || null,
        netSuiteEmployeeId: user.netSuiteEmployeeId || null,
      });
      setUser(updatedUser);
      toast.success('User details updated successfully.');
    } catch (err) {
      const errorDetails = getApiErrorDetails(err, 'Failed to update user');
      setDetailsError(errorDetails);
      toast.error(errorDetails.message);
    }
  };

  const handleChangePasswordSubmit = async (data: ChangePasswordFormData) => {
    if (!user) return;
    setPasswordError(null);
    try {
      await userService.changePassword(user.id, data.newPassword, data.confirmNewPassword);
      toast.success('Password changed successfully. The user will be required to log in again.');
    } catch (err) {
      const errorDetails = getApiErrorDetails(err, 'Failed to change password');
      setPasswordError(errorDetails);
      toast.error(errorDetails.message);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleBack}
          className="gap-1"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Users
        </Button>
        <PanelSkeleton rows={8} />
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="space-y-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleBack}
          className="gap-1"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Users
        </Button>
        <div className="flex flex-col items-center justify-center h-64 space-y-4">
          <p className="text-destructive">{error || 'User not found'}</p>
          <button
            onClick={() => window.location.reload()}
            className="text-sm text-primary hover:underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Button
        variant="ghost"
        size="sm"
        onClick={handleBack}
        className="gap-1"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Users
      </Button>

      <div className="flex items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {user.fullName || user.username}
          </h1>
          <p className="text-sm text-muted-foreground">
            @{user.username}
          </p>
        </div>
      </div>

      {user.isDefaultAdmin && (
        <Alert>
          <ShieldCheck className="h-4 w-4" />
          <AlertTitle>Protected Account</AlertTitle>
          <AlertDescription>
            This is the default System Administrator account. Its details and password cannot be modified through this interface.
            To reset the password, use the emergency reset procedure (update <code>AdminSettings__Password</code> and restart the service).
          </AlertDescription>
        </Alert>
      )}

      {!user.isDefaultAdmin && (
        <Tabs defaultValue="details">
          <TabsList>
            <TabsTrigger value="details">User Details</TabsTrigger>
            <TabsTrigger value="password">Change Password</TabsTrigger>
          </TabsList>
          <TabsContent value="details" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>User Details</CardTitle>
                <CardDescription>
                  Update the user's profile information.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <ApiErrorSummary error={detailsError} />
                <EditUserDetailsForm
                  user={user}
                  onSubmit={handleEditDetailsSubmit}
                  onCancel={handleBack}
                />
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="password" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Change Password</CardTitle>
                <CardDescription>
                  Set a new password for this user.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <ApiErrorSummary error={passwordError} />
                <EditChangePasswordForm
                  onSubmit={handleChangePasswordSubmit}
                />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
